import { useEffect, useRef, useState } from "react";
import { edges, nodes } from "../data/campusGraph";

const GOOGLE_MAPS_API_KEY = "AIzaSyDCaHGEs1kCepxCoOq4B_2T2xbT6QKPPl8";
const UW_CENTER = { lat: 43.0753, lng: -89.4013 };

export default function MapView({ result, start, end, currentMode }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [apiError, setApiError] = useState(false);

  // ── useEffect 1: Load Google Maps script ──────────────────────────────
  useEffect(() => {
    if (window.google) { setMapLoaded(true); return; }
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === "YOUR_API_KEY_HERE") { setApiError(true); return; }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,directions`;
    script.async = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setApiError(true);
    document.head.appendChild(script);
  }, []);

  // ── useEffect 2: Initialize map ───────────────────────────────────────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || apiError) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: UW_CENTER,
      zoom: 15,
      mapTypeId: "roadmap",
      styles: darkMapStyles,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
    });
    mapInstanceRef.current = map;
    Object.values(nodes).forEach((node) => {
      const marker = new window.google.maps.Marker({
        position: { lat: node.lat, lng: node.lng },
        map,
        title: node.name,
        label: { text: node.icon, fontSize: "18px" },
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0 },
      });
      markersRef.current.push(marker);
    });
  }, [mapLoaded, apiError]);

  // ── useEffect 3: Draw route ───────────────────────────────────────────
  useEffect(() => {
    if (!mapInstanceRef.current || !result || !window.google) return;
    if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);

    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor: currentMode?.color || "#10b981",
        strokeWeight: 6,
        strokeOpacity: 0.9,
      },
    });
    directionsRenderer.setMap(mapInstanceRef.current);
    directionsRendererRef.current = directionsRenderer;

    const origin = new window.google.maps.LatLng(nodes[result.path[0]].lat, nodes[result.path[0]].lng);
    const destination = new window.google.maps.LatLng(nodes[result.path[result.path.length - 1]].lat, nodes[result.path[result.path.length - 1]].lng);

    const isFastest = currentMode?.id === "fastest";
    const waypoints = isFastest ? [] : result.path.slice(1, -1).map((nodeId) => ({
      location: new window.google.maps.LatLng(nodes[nodeId].lat, nodes[nodeId].lng),
      stopover: true,
    }));

    directionsService.route(
      { origin, destination, waypoints, optimizeWaypoints: false, travelMode: window.google.maps.TravelMode.WALKING },
      (res, status) => {
        if (status === "OK") {
          directionsRenderer.setDirections(res);
        } else {
          console.warn("Directions failed:", status);
        }
      }
    );
  }, [result, currentMode]);

  if (apiError) {
    return <MockMap nodes={nodes} edges={edges} result={result} currentMode={currentMode} start={start} end={end} />;
  }

  return (
    <div className="map-container">
      {!mapLoaded && (
        <div className="map-loading">
          <div className="loading-spinner" />
          <p>Loading campus map...</p>
        </div>
      )}
      <div ref={mapRef} className="google-map" />
    </div>
  );
}

function MockMap({ nodes: nodesData, edges: edgesData, result, currentMode, start, end }) {
  const W = 700;
  const H = 500;
  const lats = Object.values(nodesData).map((n) => n.lat);
  const lngs = Object.values(nodesData).map((n) => n.lng);
  const minLat = Math.min(...lats); const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs); const maxLng = Math.max(...lngs);
  const toSVG = (lat, lng) => ({
    x: ((lng - minLng) / (maxLng - minLng)) * (W - 80) + 40,
    y: ((maxLat - lat) / (maxLat - minLat)) * (H - 80) + 40,
  });
  const activePath = result?.path || [];
  const activeSet = new Set();
  for (let i = 0; i < activePath.length - 1; i++) {
    activeSet.add(`${activePath[i]}-${activePath[i + 1]}`);
    activeSet.add(`${activePath[i + 1]}-${activePath[i]}`);
  }
  const modeColor = currentMode?.color || "#10b981";
  return (
    <div className="map-container mock-map">
      <div className="mock-badge">📍 Demo Mode — Add your Google Maps API key to see the real map</div>
      <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`}>
        <rect width={W} height={H} fill="#0f172a" rx="12" />
        {[...Array(10)].map((_, i) => (<line key={`h${i}`} x1="0" y1={i * 55} x2={W} y2={i * 55} stroke="#1e293b" strokeWidth="1" />))}
        {[...Array(14)].map((_, i) => (<line key={`v${i}`} x1={i * 55} y1="0" x2={i * 55} y2={H} stroke="#1e293b" strokeWidth="1" />))}
        {edgesData.map((edge) => {
          const from = nodesData[edge.from]; const to = nodesData[edge.to];
          if (!from || !to) return null;
          const fp = toSVG(from.lat, from.lng); const tp = toSVG(to.lat, to.lng);
          const isActive = activeSet.has(`${edge.from}-${edge.to}`);
          return (<line key={edge.id} x1={fp.x} y1={fp.y} x2={tp.x} y2={tp.y} stroke={isActive ? modeColor : "#334155"} strokeWidth={isActive ? 4 : 1.5} strokeDasharray={isActive ? "none" : "6,4"} opacity={isActive ? 1 : 0.6} />);
        })}
        {Object.values(nodesData).map((node) => {
          const pt = toSVG(node.lat, node.lng);
          const isStart = node.id === start; const isEnd = node.id === end;
          const isOnPath = activePath.includes(node.id);
          const r = isStart || isEnd ? 10 : isOnPath ? 8 : 5;
          return (
            <g key={node.id}>
              <circle cx={pt.x} cy={pt.y} r={r} fill={isStart ? "#f59e0b" : isEnd ? "#ef4444" : isOnPath ? modeColor : "#475569"} stroke="#fff" strokeWidth={isOnPath ? 2 : 1} />
              <text x={pt.x} y={pt.y - r - 4} textAnchor="middle" fontSize="10" fill={isOnPath ? "#fff" : "#94a3b8"} fontFamily="monospace">{node.name.split(" ")[0]}</text>
            </g>
          );
        })}
        <g transform="translate(16, 16)">
          <rect width="140" height="68" rx="6" fill="#1e293b" opacity="0.9" />
          <circle cx="16" cy="16" r="6" fill="#f59e0b" />
          <text x="28" y="20" fill="#e2e8f0" fontSize="11" fontFamily="monospace">Start</text>
          <circle cx="16" cy="36" r="6" fill="#ef4444" />
          <text x="28" y="40" fill="#e2e8f0" fontSize="11" fontFamily="monospace">End</text>
          <line x1="10" y1="56" x2="22" y2="56" stroke={modeColor} strokeWidth="3" />
          <text x="28" y="60" fill="#e2e8f0" fontSize="11" fontFamily="monospace">Active route</text>
        </g>
      </svg>
    </div>
  );
}

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0f172a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1e293b" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#334155" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0c4a6e" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#14532d" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
];